import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  alpha,
  useTheme,
  IconButton,
  Slide,
} from "@mui/material";
import {
  Receipt,
  Search,
  ChevronLeft,
  ChevronRight,
  Phone,
  Clear,
  KeyboardAlt,
  ViewModule,
  ViewList,
} from "@mui/icons-material";
import POSKeyboard from "./POSKeyboard";
import { toast } from "react-toastify";
import POSHeader from "./POSHeader";
import OrderStatusNavigation from "./OrderStatusNavigation";
import DashboardOrderCard from "./Dashboard/DashboardOrderCard";
import OrdersListTable from "./Dashboard/OrdersListTable";
import OrderDetailsScreen from "./OrderDetailsScreen";
import OrderServices from "../../../services/OrderService";
import DashboardService from "../../../services/DashboardService";
import { useUserWithCompany } from "../../../hooks/useCompany";

const OrdersScreen = ({
  mainTab,
  setMainTab,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  handleBackToTableSelect,
  handleTablesClick,
  handleOffersClick,
  onPOSClick,
  onHomeClick,
  initialStatus = "all", // Default filter from parent
  onStatusChange, // Callback when status changes
  initialOrderId = null, // Initial order to display (from dashboard click)
  onEditOrder, // Callback to navigate to order placement with pre-filled cart
}) => {
  // Get company data
  const { companyId } = useUserWithCompany();

  // Build today's order-number prefix: YY-MMDD-  (e.g. "26-218-" for 2026-Feb-18)
  const _now = new Date();
  const _orderPrefix = `${String(_now.getFullYear()).slice(2)}-${_now.getMonth() + 1}${_now.getDate()}-`;

  // Local state management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderTab, setOrderTab] = useState(initialStatus);
  const [orderSearch, setOrderSearch] = useState("");
  const [customerMobileSearch, setCustomerMobileSearch] = useState("");
  const [ordersData, setOrdersData] = useState({
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
  });
  const [statusCounts, setStatusCounts] = useState({});

  // Sync orderTab with initialStatus when it changes
  useEffect(() => {
    setOrderTab(initialStatus);
  }, [initialStatus]);

  // Notify parent when status changes
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(orderTab);
    }
  }, [orderTab]);
  const theme = useTheme();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderOpenedFromDashboard, setOrderOpenedFromDashboard] =
    useState(false);
  const [viewMode, setViewMode] = useState("grid");

  // null | "order" | "mobile"
  const [activeKb, setActiveKb] = useState(null);
  const toggleKb = (field) =>
    setActiveKb((prev) => (prev === field ? null : field));

  // Set selectedOrder from initialOrderId when navigating from dashboard
  useEffect(() => {
    if (initialOrderId) {
      setSelectedOrder(initialOrderId);
      setOrderOpenedFromDashboard(true);
    }
  }, [initialOrderId]);

  // Fetch status counts from dashboard API
  const fetchStatusCounts = async () => {
    if (!companyId) return;
    try {
      const response = await DashboardService.getDashboardStats(companyId);
      const ordersByStatus = response?.data?.view?.ordersByStatus || {};
      setStatusCounts(ordersByStatus);
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    }
  };

  // Fetch orders API function
  const fetchOrders = async (
    page = 0,
    customerMobile = "",
    status = null,
    orderNumber = "",
  ) => {
    if (!companyId) return;

    try {
      setLoading(true);
      const params = {
        companyId,
        page,
        limit: 16,
      };

      // Only add status param if it's not "all"
      if (status && status !== "all") {
        params.status = status;
      }

      if (customerMobile && customerMobile.trim()) {
        params.customerMobile = customerMobile.trim();
      }

      // Prepend date prefix to form full order number (e.g. "26-33-5")
      if (orderNumber && orderNumber.trim()) {
        params.orderNumber = `${_orderPrefix}${orderNumber.trim()}`;
      }

      const response = await OrderServices.getAllOrders(params);
      console.log("orders", response);

      if (response && response.data?.view?.data) {
        const listData = response.data.view.data;

        // Fetch full details for each order so cards can always render OrderItems
        const ordersWithDetails = await Promise.all(
          listData.map(async (order) => {
            try {
              const detailsResponse = await OrderServices.getOrderById(
                order.id,
                { companyId },
              );

              if (detailsResponse?.data?.view) {
                const details = detailsResponse.data.view;
                return {
                  ...order,
                  ...details,
                  OrderItems:
                    details.OrderItems ||
                    details.orderItems ||
                    order.OrderItems ||
                    order.orderItems ||
                    [],
                };
              }
              return order;
            } catch (error) {
              console.error(
                `Error fetching details for order ${order.id}:`,
                error,
              );
              return order;
            }
          }),
        );

        setOrders(ordersWithDetails);
        setOrdersData({
          data: ordersWithDetails,
          totalItems:
            response.data.view.totalItems || ordersWithDetails.length,
          totalPages:
            response.data.view.totalPages ||
            Math.ceil(
              (response.data.view.totalItems ||
                ordersWithDetails.length) / 16,
            ),
          currentPage: page,
        });
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders when orderTab changes
  useEffect(() => {
    if (!companyId) return;

    // Clear orders immediately to prevent flash of old data
    setOrders([]);
    setOrdersData({
      data: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
    });

    fetchOrders(0, customerMobileSearch, orderTab);
    fetchStatusCounts();

    // Auto-refresh orders every 30 seconds
    const interval = setInterval(() => {
      fetchOrders(0, customerMobileSearch, orderTab);
      fetchStatusCounts();
    }, 30000);

    return () => clearInterval(interval);
  }, [orderTab, companyId]);

  // Debounced effect for customerMobileSearch
  useEffect(() => {
    if (!companyId) return;

    const debounceTimer = setTimeout(() => {
      fetchOrders(0, customerMobileSearch, orderTab, orderSearch);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [customerMobileSearch, companyId]); // Removed orderTab from dependencies

  // Debounced effect for orderSearch (order number)
  useEffect(() => {
    if (!companyId) return;

    const debounceTimer = setTimeout(() => {
      fetchOrders(0, customerMobileSearch, orderTab, orderSearch);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [orderSearch, companyId]);

  // Handle order selection
  const handleOrderClick = (orderId) => {
    setSelectedOrder(orderId);
    setOrderOpenedFromDashboard(false);
  };

  const handleBackToOrders = () => {
    setOrderOpenedFromDashboard(false);
    setSelectedOrder(null);
    // Refetch orders immediately to show updated data
    fetchOrders(currentPage, customerMobileSearch, orderTab, orderSearch);
  };

  // Pagination setup
  const itemsPerPage = 16;
  const totalOrders = ordersData?.totalItems || orders.length;
  const totalPages =
    ordersData?.totalPages || Math.ceil(orders.length / itemsPerPage);
  const currentPage = ordersData?.currentPage || 0;

  // Use paginated data if available, otherwise slice orders
  const displayOrders = ordersData?.data || orders;

  // Pagination calculations
  const calculatePaginationDisplay = () => {
    if (totalOrders === 0) return { start: 0, end: 0, total: 0 };

    const start = currentPage * itemsPerPage + 1;
    const end = Math.min((currentPage + 1) * itemsPerPage, totalOrders);

    return { start, end, total: totalOrders };
  };

  // Navigation handlers
  const navigationHandlers = {
    changePage: (newPage) => {
      if (newPage >= 0 && newPage < totalPages) {
        fetchOrders(newPage, customerMobileSearch, orderTab);
      }
    },
    goToPreviousPage: () => navigationHandlers.changePage(currentPage - 1),
    goToNextPage: () => navigationHandlers.changePage(currentPage + 1),
  };

  // Swipe functionality
  const swipeHandlers = {
    minDistance: 50,
    onTouchStart: (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    },
    onTouchMove: (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    },
    onTouchEnd: () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > swipeHandlers.minDistance;
      const isRightSwipe = distance < -swipeHandlers.minDistance;

      if (isLeftSwipe && currentPage < totalPages - 1) {
        navigationHandlers.goToNextPage();
      }
      if (isRightSwipe && currentPage > 0) {
        navigationHandlers.goToPreviousPage();
      }
    },
  };

  // Component state checks
  const hasMultiplePages = totalPages > 1;
  const paginationDisplay = calculatePaginationDisplay();

  const getEmptyMessage = () => {
    switch (orderTab) {
      case "pending":
        return "pending orders";
      case "confirmed":
        return "confirmed orders";
      case "preparing":
        return "orders being prepared";
      case "ready":
        return "ready orders";
      case "served":
        return "served orders";
      default:
        return "orders";
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
      }}
    >
      {/* Header */}
      <POSHeader
        mainTab={mainTab}
        setMainTab={setMainTab}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        fullscreenLabel={fullscreenLabel}
        onNewOrder={handleBackToTableSelect}
        onTablesClick={handleTablesClick}
        onOffersClick={handleOffersClick}
        onPOSClick={onPOSClick}
        onHomeClick={onHomeClick}
        showNewOrderButton={true}
      />

      {/* ── Search Bar + Status Navigation ──────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#fff",
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: `0 2px 12px ${alpha("#000", 0.06)}`,
        }}
      >
        {/* Row 1 — Status Tabs (full width, touch-friendly) */}
        <Box
          sx={{
            px: 2,
            pt: 1.25,
            pb: 1,
            borderBottom: `1px solid ${alpha("#000", 0.06)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.primary.dark, 0.07)} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <OrderStatusNavigation
              orderTab={orderTab}
              setOrderTab={setOrderTab}
              statusCounts={statusCounts}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.35,
              p: 0.5,
              borderRadius: "12px",
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              bgcolor: alpha(theme.palette.grey[100], 0.75),
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setViewMode("grid")}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                bgcolor:
                  viewMode === "grid"
                    ? alpha("#667eea", 0.16)
                    : "transparent",
                color:
                  viewMode === "grid"
                    ? "#667eea"
                    : theme.palette.text.secondary,
                "&:hover": {
                  bgcolor:
                    viewMode === "grid"
                      ? alpha("#667eea", 0.2)
                      : alpha(theme.palette.grey[300], 0.45),
                },
              }}
            >
              <ViewModule sx={{ fontSize: 24 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode("list")}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                bgcolor:
                  viewMode === "list"
                    ? alpha("#667eea", 0.16)
                    : "transparent",
                color:
                  viewMode === "list"
                    ? "#667eea"
                    : theme.palette.text.secondary,
                "&:hover": {
                  bgcolor:
                    viewMode === "list"
                      ? alpha("#667eea", 0.2)
                      : alpha(theme.palette.grey[300], 0.45),
                },
              }}
            >
              <ViewList sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Row 2 — Search Fields */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* Order Number Search */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder="Order number…"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              onFocus={() => setActiveKb("order")}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      sx={{ color: theme.palette.primary.main, fontSize: 22 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {orderSearch ? (
                      <IconButton
                        size="small"
                        onClick={() => setOrderSearch("")}
                        sx={{
                          p: 0.5,
                          color: alpha(theme.palette.primary.main, 0.6),
                          "&:hover": {
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <Clear sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => toggleKb("order")}
                        sx={{
                          p: 0.5,
                          color:
                            activeKb === "order"
                              ? theme.palette.primary.main
                              : alpha(theme.palette.primary.main, 0.45),
                          bgcolor:
                            activeKb === "order"
                              ? alpha(theme.palette.primary.main, 0.1)
                              : "transparent",
                          borderRadius: "8px",
                        }}
                      >
                        <KeyboardAlt sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  height: 50,
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  fontFamily: "monospace",
                  "& fieldset": {
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  "&:hover fieldset": {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused": {
                    bgcolor: "#fff",
                    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.primary,
                  "&::placeholder": {
                    color: alpha(theme.palette.text.secondary, 0.5),
                    fontWeight: 500,
                    opacity: 1,
                    fontFamily: "inherit",
                  },
                },
              }}
            />
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              height: 36,
              bgcolor: alpha("#000", 0.1),
              flexShrink: 0,
            }}
          />

          {/* Customer Mobile Search */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="Customer mobile…"
              value={customerMobileSearch || ""}
              onChange={(e) => setCustomerMobileSearch(e.target.value)}
              onFocus={() => setActiveKb("mobile")}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: "#10b981", fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {customerMobileSearch ? (
                      <IconButton
                        size="small"
                        onClick={() => setCustomerMobileSearch("")}
                        sx={{
                          p: 0.5,
                          color: alpha("#10b981", 0.7),
                          "&:hover": {
                            color: "#10b981",
                            bgcolor: alpha("#10b981", 0.08),
                          },
                        }}
                      >
                        <Clear sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => toggleKb("mobile")}
                        sx={{
                          p: 0.5,
                          color:
                            activeKb === "mobile"
                              ? "#10b981"
                              : alpha("#10b981", 0.45),
                          bgcolor:
                            activeKb === "mobile"
                              ? alpha("#10b981", 0.1)
                              : "transparent",
                          borderRadius: "8px",
                        }}
                      >
                        <KeyboardAlt sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: alpha("#10b981", 0.03),
                  height: 50,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  "& fieldset": {
                    border: `2px solid ${alpha("#10b981", 0.2)}`,
                  },
                  "&:hover fieldset": { borderColor: alpha("#10b981", 0.45) },
                  "&.Mui-focused fieldset": { borderColor: "#10b981" },
                  "&.Mui-focused": {
                    bgcolor: "#fff",
                    boxShadow: `0 0 0 4px ${alpha("#10b981", 0.1)}`,
                  },
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.primary,
                  "&::placeholder": {
                    color: alpha(theme.palette.text.secondary, 0.5),
                    fontWeight: 500,
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Orders Content */}
      <Box
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          position: "relative",
          touchAction: "pan-y",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {/* Loading Overlay - Only show when loading and cards exist */}
        {loading && displayOrders.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: alpha("#fff", 0.6),
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              transition: "opacity 0.2s ease",
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: "16px",
                p: 3,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={40} thickness={4} />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                Loading orders...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Initial Loading - Only show when no orders exist yet */}
        {loading && displayOrders.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={60} thickness={4} />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                }}
              >
                Loading orders...
              </Typography>
            </Box>
          </Box>
        ) : displayOrders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Receipt
              sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }}
            />
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              No {getEmptyMessage()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Orders will appear here once they are placed
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              opacity: loading ? 0.5 : 1,
              transition: "opacity 0.2s ease",
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {viewMode === "list" ? (
              <OrdersListTable
                orders={displayOrders}
                onSelectOrder={(order) => handleOrderClick(order.id)}
              />
            ) : (
              <Grid container spacing={2}>
                {displayOrders.map((order) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={order.id}>
                    <DashboardOrderCard
                      order={order}
                      enforceFixedHeight={false}
                      onClick={() => handleOrderClick(order.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* Pagination Navigation - Fixed at bottom */}
      {hasMultiplePages && (
        <Box
          sx={{
            bgcolor: "white",
            borderTop: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.08),
            boxShadow: `0 -1px 3px ${alpha("#000", 0.04)}`,
            p: 2,
            flexShrink: 0,
            zIndex: 1000,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Left Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={navigationHandlers.goToPreviousPage}
                disabled={currentPage === 0}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  bgcolor:
                    currentPage === 0
                      ? alpha("#000", 0.05)
                      : alpha("#667eea", 0.1),
                  color: currentPage === 0 ? alpha("#000", 0.3) : "#667eea",
                  border: `1px solid ${currentPage === 0 ? alpha("#000", 0.1) : alpha("#667eea", 0.2)}`,
                  "&:hover": {
                    bgcolor:
                      currentPage === 0
                        ? alpha("#000", 0.05)
                        : alpha("#667eea", 0.15),
                    transform: currentPage === 0 ? "none" : "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ChevronLeft sx={{ fontSize: 20 }} />
              </IconButton>
              {/* Pagination Info - Left Side */}
              {paginationDisplay.total > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  Showing {paginationDisplay.start}-{paginationDisplay.end} of{" "}
                  {paginationDisplay.total} orders
                </Typography>
              )}
            </Box>

            {/* Page Indicator Dots - Center */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {Array.from({ length: totalPages }, (_, index) => (
                <Box
                  key={index}
                  onClick={() => navigationHandlers.changePage(index)}
                  sx={{
                    width: index === currentPage ? 24 : 8,
                    height: 8,
                    borderRadius: "4px",
                    bgcolor:
                      index === currentPage ? "#667eea" : alpha("#667eea", 0.2),
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor:
                        index === currentPage
                          ? "#5a67d8"
                          : alpha("#667eea", 0.4),
                      transform: "scale(1.1)",
                    },
                  }}
                />
              ))}
            </Box>

            {/* Right Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Swipe to navigate
              </Typography>
              <IconButton
                onClick={navigationHandlers.goToNextPage}
                disabled={currentPage === totalPages - 1}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  bgcolor:
                    currentPage === totalPages - 1
                      ? alpha("#000", 0.05)
                      : alpha("#667eea", 0.1),
                  color:
                    currentPage === totalPages - 1
                      ? alpha("#000", 0.3)
                      : "#667eea",
                  border: `1px solid ${currentPage === totalPages - 1 ? alpha("#000", 0.1) : alpha("#667eea", 0.2)}`,
                  "&:hover": {
                    bgcolor:
                      currentPage === totalPages - 1
                        ? alpha("#000", 0.05)
                        : alpha("#667eea", 0.15),
                    transform:
                      currentPage === totalPages - 1 ? "none" : "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ChevronRight sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Order Details Drawer - Sliding from bottom */}
      <Slide
        direction="up"
        in={selectedOrder !== null}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: "#f8fafc",
          }}
        >
          <OrderDetailsScreen
            orderId={selectedOrder}
            onBack={orderOpenedFromDashboard ? onPOSClick : handleBackToOrders}
            onPOSClick={onPOSClick}
            onOrderUpdated={() =>
              fetchOrders(currentPage, customerMobileSearch, orderTab)
            }
            onEditOrder={(
              cart,
              table,
              existingOrderId,
              existingOrderNumber,
            ) => {
              handleBackToOrders();
              if (onEditOrder) {
                onEditOrder(cart, table, existingOrderId, existingOrderNumber);
              }
            }}
          />
        </Box>
      </Slide>

      {/* Floating POS Keyboard — slides up from bottom on search focus */}
      <Slide direction="up" in={!!activeKb} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            p: 1.5,
            pb: 2,
            background: `linear-gradient(to top, rgba(10,12,24,0.85) 0%, transparent 100%)`,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <POSKeyboard
              value={activeKb === "order" ? orderSearch : customerMobileSearch}
              onChange={(v) => {
                if (activeKb === "order") setOrderSearch(v);
                else setCustomerMobileSearch(v);
              }}
              onClose={() => setActiveKb(null)}
              onEnter={() => {
                setActiveKb(null);
                fetchOrders(0, customerMobileSearch, orderTab, orderSearch);
              }}
              numericOnly={activeKb === "mobile"}
            />
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default OrdersScreen;
